/**
 * Slideshow Hotspot Component
 * Handles interactive hotspots on slideshow slides with product and custom content
 */

if (!window.SlideshowHotspot) {
  window.SlideshowHotspot = class SlideshowHotspot {
  constructor(slideElement) {
    this.slide = slideElement;
    this.hotspots = this.slide.querySelectorAll('[js-slideshow-hotspot]');
    this.popup = this.slide.querySelector('[js-slideshow-hotspot-popup]');
    this.popupContent = this.slide.querySelector('[js-slideshow-hotspot-content]');
    this.closeButtons = this.slide.querySelectorAll('[js-slideshow-hotspot-close]');
    this.slideshowSection = this.slide.closest('slideshow-section');
    this.mediaWrapper = this.slide.querySelector('.slideshow-slide__media-wrapper');
    
    // Get the actual img element inside the media wrapper
    // Try multiple selectors to find the image
    this.slideImage = this.mediaWrapper?.querySelector('img') || 
                      this.mediaWrapper?.querySelector('.slideshow-slide__media--image img') ||
                      this.slide.querySelector('img');
    
    console.log('Constructor - found slideImage:', this.slideImage);
    
    this.productCache = new Map();
    this.wasAutoplayPaused = false;
    
    this.init();
  }

  init() {
    if (!this.hotspots.length || !this.popup) return;

    // Wait for images to load before calculating heights
    this.waitForImagesAndApplyHeights();
    
    // Recalculate on window resize
    window.addEventListener('resize', () => this.applyDynamicHeights());

    // Bind hotspot click events
    this.hotspots.forEach(hotspot => {
      hotspot.addEventListener('click', (e) => this.handleHotspotClick(e));
    });

    // Bind close events
    this.closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        this.closePopup();
      });
    });

    // Handle clicks when popover is open
    this.slide.addEventListener('click', (e) => {
      if (!this.popup.hasAttribute('hidden')) {
        const clickedInsidePopup = this.popup.contains(e.target);
        const clickedHotspot = Array.from(this.hotspots).some(hotspot => hotspot.contains(e.target));
        
        // If clicking inside popover, stop propagation (keep it open, no slide change)
        if (clickedInsidePopup) {
          e.stopPropagation();
          return;
        }
        
        // If clicking outside popover (but not on a hotspot), close it and stop propagation
        if (!clickedHotspot) {
          e.stopPropagation();
          this.closePopup();
        }
      }
    }, true); // Use capture phase to intercept before slideshow handler

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.popup.hasAttribute('hidden')) {
        this.closePopup();
      }
    });
  }

  waitForImagesAndApplyHeights() {
    if (!this.mediaWrapper) return;

    const images = this.mediaWrapper.querySelectorAll('img');
    const imageLoadPromises = Array.from(images).map(img => {
      if (img.complete && img.naturalWidth > 0) {
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        img.addEventListener('load', resolve);
        img.addEventListener('error', resolve); // Resolve even on error to not block
      });
    });

    // Use Intersection Observer to wait for section to be visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Section is now visible, calculate heights
          this.applyDynamicHeights();
          
          // Re-apply after images are loaded (for natural ratios)
          Promise.all(imageLoadPromises).then(() => {
            this.applyDynamicHeights();
            
            // Additional recalculation after animation completes
            setTimeout(() => {
              this.applyDynamicHeights();
            }, 1500);
          });
          
          // Stop observing after first calculation
          observer.unobserve(this.slideshowSection);
        }
      });
    }, {
      threshold: 0.01, // Trigger when even 1% is visible
      rootMargin: '50px' // Start calculating slightly before it's in view
    });

    // Start observing the slideshow section
    if (this.slideshowSection) {
      observer.observe(this.slideshowSection);
    }

    // Also add resize listener to recalculate on window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.applyDynamicHeights();
      }, 150);
    });
  }

  applyDynamicHeights() {
    if (!this.mediaWrapper || !this.slideshowSection) return;

    const desktopAspectRatio = this.mediaWrapper.dataset.desktopAspectRatio;
    const mobileAspectRatio = this.mediaWrapper.dataset.mobileAspectRatio;
    
    if (!desktopAspectRatio || !mobileAspectRatio) return;

    const slideshow = this.slideshowSection.querySelector('.slideshow--hotspot');
    if (!slideshow) return;

    // Get the current width of the slideshow
    const slideshowWidth = slideshow.offsetWidth;
    
    // Aspect ratio mapping (same as used in image snippet)
    const aspectRatioMap = {
      'natural': null, // Will be calculated from actual image
      'portrait': 2/3,      // 2:3
      'portrait-alt': 3/4,  // 3:4
      'square': 1,          // 1:1
      'landscape-alt': 4/3, // 4:3
      'landscape': 3/2      // 3:2
    };

    // Helper function to get natural aspect ratio from image
    const getNaturalRatio = (imageElement) => {
      if (!imageElement) {
        console.log('Image element not found');
        return null;
      }
      if (!imageElement.naturalWidth || !imageElement.naturalHeight) {
        console.log('Image dimensions not available yet:', imageElement);
        return null;
      }
      const ratio = imageElement.naturalWidth / imageElement.naturalHeight;
      console.log('Natural ratio calculated:', ratio, `(${imageElement.naturalWidth}x${imageElement.naturalHeight})`);
      return ratio;
    };

    // Get desktop image - try multiple selectors
    let desktopImage = this.mediaWrapper.querySelector('.slideshow-slide__media--image-desktop');
    // If the element with that class IS the img, use it, otherwise find img inside
    if (desktopImage && desktopImage.tagName !== 'IMG') {
      desktopImage = desktopImage.querySelector('img');
    }
    console.log('Desktop image element:', desktopImage, 'Natural dimensions:', desktopImage?.naturalWidth, 'x', desktopImage?.naturalHeight);
    
    // Calculate desktop height
    let desktopRatio = aspectRatioMap[desktopAspectRatio];
    if (desktopAspectRatio === 'natural') {
      desktopRatio = getNaturalRatio(desktopImage);
      // Fallback to landscape if natural ratio not available yet
      if (!desktopRatio) {
        desktopRatio = 3/2; // Default to landscape
        console.log('Using fallback desktop ratio: 3/2');
      }
    }
    
    let desktopHeight = `${slideshowWidth / desktopRatio}px`;

    // Get mobile image - try multiple selectors
    let mobileImage = this.mediaWrapper.querySelector('.slideshow-slide__media--image-mobile');
    // If the element with that class IS the img, use it, otherwise find img inside
    if (mobileImage && mobileImage.tagName !== 'IMG') {
      mobileImage = mobileImage.querySelector('img');
    }
    console.log('Mobile image element:', mobileImage, 'Natural dimensions:', mobileImage?.naturalWidth, 'x', mobileImage?.naturalHeight);
    
    // Calculate mobile height
    let mobileRatio = aspectRatioMap[mobileAspectRatio];
    if (mobileAspectRatio === 'natural') {
      // Use mobile image if available, otherwise fall back to desktop image
      mobileRatio = getNaturalRatio(mobileImage) || getNaturalRatio(desktopImage);
      // Fallback to portrait if natural ratio not available yet
      if (!mobileRatio) {
        mobileRatio = 2/3; // Default to portrait
        console.log('Using fallback mobile ratio: 2/3');
      }
    }
    
    let mobileHeight = `${slideshowWidth / mobileRatio}px`;

    // Apply CSS variables to the slideshow
    slideshow.style.setProperty('--custom-desktop-height', desktopHeight);
    slideshow.style.setProperty('--custom-mobile-height', mobileHeight);

    console.log('Applied dynamic heights:', {
      width: slideshowWidth,
      desktopAspectRatio,
      mobileAspectRatio,
      desktopRatio,
      mobileRatio,
      desktopHeight,
      mobileHeight
    });
  }

  async handleHotspotClick(event) {
    // Prevent click from bubbling to slideshow
    event.stopPropagation();
    event.preventDefault();
    
    const hotspot = event.currentTarget;
    const hotspotType = hotspot.dataset.hotspotType;
    const hotspotId = hotspot.dataset.hotspotId;
    const popoverPosition = hotspot.dataset.popoverPosition || 'right';

    if (hotspotType === 'product_hotspot') {
      const productHandle = hotspot.dataset.productHandle;
      if (productHandle) {
        await this.showProductPopup(productHandle);
      }
    } else if (hotspotType === 'custom_hotspot') {
      this.showCustomPopup(hotspotId);
    }

    this.openPopup(hotspot, popoverPosition);
  }

  async showProductPopup(handle) {
    // Show loading state
    this.popupContent.innerHTML = `
      <div class="slideshow-hotspot-product__loading">
        Loading product...
      </div>
    `;

    try {
      // Check cache first
      let product = this.productCache.get(handle);
      
      if (!product) {
        // Fetch product data from .json endpoint
        const response = await fetch(`/products/${handle}.json`);
        if (!response.ok) throw new Error('Product not found');
        const data = await response.json();
        // Extract product from wrapper
        product = data.product;
        this.productCache.set(handle, product);
      }

      // Render product content
      this.renderProductContent(product);
    } catch (error) {
      console.error('Error fetching product:', error);
      this.popupContent.innerHTML = `
        <div class="slideshow-hotspot-product__error">
          Unable to load product. Please try again.
        </div>
      `;
    }
  }

  renderProductContent(product) {
    const firstVariant = product.variants[0];
    
    // Handle price - .json returns string like "45.00", not cents
    const price = `$${firstVariant.price}`;
    
    const priceHTML = `<span class="slideshow-hotspot-product__price">${price}</span>`;
    
    // Get product URL and image
    const productUrl = `/products/${product.handle}`;
    const productImage = product.image?.src || product.images?.[0]?.src || '';
    
    this.popupContent.innerHTML = `
      <a href="${productUrl}" class="slideshow-hotspot-product">
        <div class="slideshow-hotspot-product__image-wrapper">
          <img 
            src="${productImage}" 
            alt="${product.title}"
            class="slideshow-hotspot-product__image"
          />
        </div>
        <div class="slideshow-hotspot-product__info">
          <h4 class="slideshow-hotspot-product__title">${product.title}</h4>
          ${priceHTML}
        </div>
        <div class="slideshow-hotspot-product__redirect">
          <button class="slideshow-hotspot-product__button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask0_7956_6705" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
              <rect width="24" height="24" fill="#D9D9D9"/>
              </mask>
              <g mask="url(#mask0_7956_6705)">
              <path d="M11.9994 19.2254L11.4744 18.6754L17.7494 12.3754H4.77441V11.6254H17.7494L11.4744 5.32539L11.9994 4.77539L19.2244 12.0004L11.9994 19.2254Z" fill="#202718"/>
              </g>
            </svg>
          </button>
        </div>
      </a>
    `;
  }


  showCustomPopup(hotspotId) {
    const template = this.slide.querySelector(`template[data-hotspot-content="${hotspotId}"]`);
    if (!template) return;

    const content = template.content.cloneNode(true);
    this.popupContent.innerHTML = '';
    this.popupContent.appendChild(content);
  }

  openPopup(hotspot, position) {
    // Remove any existing position classes
    this.popup.classList.remove('slideshow-hotspot-popup--top', 'slideshow-hotspot-popup--bottom', 'slideshow-hotspot-popup--left', 'slideshow-hotspot-popup--right');
    
    // Add the new position class
    this.popup.classList.add(`slideshow-hotspot-popup--${position}`);
    
    // Position the popup relative to the hotspot using CSS variables
    const hotspotRect = hotspot.getBoundingClientRect();
    const slideRect = this.slide.getBoundingClientRect();
    
    // Calculate position relative to slide
    const relativeX = hotspotRect.left - slideRect.left + (hotspotRect.width / 2);
    const relativeY = hotspotRect.top - slideRect.top + (hotspotRect.height / 2);
    
    // Set CSS variables instead of inline styles so media queries can override
    this.popup.style.setProperty('--popup-x', `${relativeX}px`);
    this.popup.style.setProperty('--popup-y', `${relativeY}px`);
    
    this.popup.removeAttribute('hidden');
    
    // Pause slideshow autoplay
    this.pauseSlideshow();
  }

  closePopup() {
    this.popup.setAttribute('hidden', '');
    
    // Resume slideshow autoplay if it was playing
    this.resumeSlideshow();
  }

  pauseSlideshow() {
    if (this.slideshowSection) {
      const isPaused = this.slideshowSection.dataset.paused === 'true';
      this.wasAutoplayPaused = isPaused;
      
      if (!isPaused) {
        this.slideshowSection.dataset.paused = 'true';
        this.slideshowSection.pauseAutoplay?.();
      }
    }
  }

  resumeSlideshow() {
    if (this.slideshowSection && !this.wasAutoplayPaused) {
      this.slideshowSection.dataset.paused = 'false';
      this.slideshowSection.resumeAutoplay?.();
    }
  }

  formatMoney(cents) {
    // Basic money formatting - adjust based on your theme's money format
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  };
}

// Initialize function that can be called multiple times
function initializeSlideshowHotspots() {
  const hotspotSlides = document.querySelectorAll('[js-slideshow-hotspot-slide]:not([data-hotspot-initialized])');
  hotspotSlides.forEach(slide => {
    slide.setAttribute('data-hotspot-initialized', 'true');
    new window.SlideshowHotspot(slide);
  });

  // Initialize navigation arrows
  const navArrows = document.querySelectorAll('[js-slideshow-nav-arrow]:not([data-nav-initialized])');
  navArrows.forEach(arrow => {
    arrow.setAttribute('data-nav-initialized', 'true');
    arrow.addEventListener('click', (e) => {
      const direction = e.currentTarget.getAttribute('data-direction');
      const slideshowSection = e.currentTarget.closest('slideshow-section');
      
      if (slideshowSection) {
        const paginationNav = slideshowSection.querySelector(
          `[js-slideshow-pagination-nav][data-direction="${direction}"]`
        );
        
        if (paginationNav) {
          paginationNav.click();
        }
      }
    });
  });
}

// Initialize immediately if DOM is ready, otherwise wait
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSlideshowHotspots);
} else {
  initializeSlideshowHotspots();
}

// Also run on each script load (for multiple section instances)
initializeSlideshowHotspots();
